<template>
  <div class="gs-section">
    <div class="gs-section__toolbar">
      <div class="gs-segmented" role="group" aria-label="Social network">
        <button
          v-for="network in networks"
          :key="network.value"
          type="button"
          class="gs-segmented__item"
          :class="{ 'is-current': currentNetwork === network.value }"
          :aria-pressed="currentNetwork === network.value ? 'true' : 'false'"
          :data-group="network.value"
          @click="selectNetwork(network.value)"
        >
          {{ network.title }}
        </button>
      </div>
      <p v-if="posts.length" class="gs-section__count">{{ countLabel }}</p>
    </div>

    <div v-if="posts.length" class="gs-asset-grid gs-asset-grid--wide">
      <asset-card
        v-for="post in posts"
        :key="`${currentNetwork}-${post.id}`"
        collapsible
        :title="post.name"
      >
        <template slot="preview">
          <pre class="gs-asset-text">{{ post.body }}</pre>
        </template>

        <template slot="actions">
          <copy-button
            :text="post.body"
            label="Copy post"
            title="Copy post"
          />

          <span class="gs-share">
            <vue-goodshare-facebook
              v-if="currentNetwork === 'facebook'"
              :page_url="post.link"
              :page_description="post.body"
              :quote="post.body"
              title_social="Share On Facebook"
              has_icon
            ></vue-goodshare-facebook>
            <vue-goodshare-twitter
              v-else-if="currentNetwork === 'twitter'"
              :page_url="post.link"
              :page_title="post.body"
              :page_description="post.body"
              :quote="post.body"
              title_social="Share On Twitter"
              has_icon
            ></vue-goodshare-twitter>
            <vue-goodshare-linked-in
              v-else
              :page_url="post.link"
              :page_description="post.body"
              :quote="post.body"
              title_social="Share On LinkedIn"
              has_icon
            ></vue-goodshare-linked-in>
          </span>
        </template>
      </asset-card>
    </div>

    <div v-else class="gs-empty">
      <span class="gs-empty__icon"><gs-icon name="share" size="22" /></span>
      <p class="gs-empty__title">{{ emptyTitle }}</p>
      <p class="gs-empty__text">
        Ready-to-post updates for this network show up here.
      </p>
    </div>
  </div>
</template>

<script>
import VueGoodshareFacebook from 'vue-goodshare/src/providers/Facebook.vue';
import VueGoodshareTwitter from 'vue-goodshare/src/providers/Twitter.vue';
import VueGoodshareLinkedIn from 'vue-goodshare/src/providers/LinkedIn.vue';
import AssetCard from '@/components/promotools/AssetCard.vue';
import CopyButton from '@/components/promotools/CopyButton.vue';

export default {
  components: {
    VueGoodshareFacebook,
    VueGoodshareTwitter,
    VueGoodshareLinkedIn,
    AssetCard,
    CopyButton,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      networks: [
        { title: this.$t('frontend_resources_facebook'), value: 'facebook' },
        { title: 'X', value: 'twitter' },
        { title: this.$t('frontend_resources_linkedin'), value: 'linkedin' },
      ],
      currentNetwork: 'facebook',
    };
  },
  computed: {
    // One card list for all three networks: the payload only differs in which
    // key holds the copy (`tweet` on X, `post` everywhere else).
    posts() {
      const source = (this.funnelResource && this.funnelResource.social_media_posts) || {};
      const list = source[this.currentNetwork] || [];

      return list
        .filter(post => post.enabled)
        .map(post => ({
          id: post.id,
          name: post.name,
          body: this.currentNetwork === 'twitter' ? post.tweet : post.post,
          link: post.affiliate_link,
        }));
    },
    countLabel() {
      const count = this.posts.length;
      return `${count} ${count === 1 ? 'post' : 'posts'}`;
    },
    emptyTitle() {
      if (this.currentNetwork === 'facebook') {
        return this.$t('frontend_resources_no_facebook_posts');
      }

      if (this.currentNetwork === 'twitter') {
        return this.$t('frontend_resources_no_tweets');
      }

      return this.$t('frontend_resources_no_linked_in');
    },
  },
  methods: {
    selectNetwork(value) {
      this.currentNetwork = value;
    },
  },
};
</script>

<style scoped>
.gs-share {
  display: inline-flex;
}

.gs-share ::v-deep .button-social {
  margin: 0;
}
</style>
